import { Platform, StyleSheet } from 'react-native';
import Constants from 'expo-constants';
import { MD3LightTheme as PaperDefaultTheme } from 'react-native-paper';
import { v4 as uuidv4 } from 'uuid';

const VERSION = Constants.expoConfig.version;
const MAJOR_VERSION_NUMBER = Constants.expoConfig.extra.majorVersion;
const MINOR_VERSION_NUMBER = Constants.expoConfig.extra.minorVersion;
const PATCH_VERSION_NUMBER = Constants.expoConfig.extra.patchVersion;

const IS_DEVELOPMENT = false;
const DEFAULT_DEBUG_LEVEL = false;

const HOMEPAGE_LINK = "https://findhumane.com/";
const APPLE_STORE_LINK = "https://apps.apple.com/app/find-humane/id6444166104";
const ANDROID_STORE_LINK = "https://play.google.com/store/apps/details?id=com.findhumane.app";
const SUBSTACK_LINK = "https://blog.findhumane.com/";
const TWITTER_LINK = "https://x.com/findhumane";
const INSTAGRAM_LINK = "https://www.instagram.com/findhumane/";
const FACEBOOK_LINK = "https://www.facebook.com/findhumane/";
const B_CORP_LINK = "https://blog.findhumane.com/p/pending-certified-b-corporation";
const GRANT_LINK = "https://blog.findhumane.com/p/find-humane-receives-31178-grant";
const GRANT_LINK_2023 = "https://blog.findhumane.com/p/find-humane-receives-25000-grant";
const HEADER_TEXT_OVERFLOW_LENGTH = 15;
const SMALL_SCREEN_WIDTH1 = 500;
const SUBSCRIBE_POPUP_DELAY_MILLISECONDS = IS_DEVELOPMENT ? 10000 : 60000;
const GOOGLE_API_KEY_AUTOCOMPLETE = Constants.expoConfig.extra.googleNoRestrictionsKey;
const EMAIL_REGEX = /\S+@\S+\.\S+/;

// https://support.google.com/analytics/answer/1033863
const UTM_SOURCES = Object.freeze({
  FINDHUMANE: "findhumane",
});

// The advertising or marketing medium
const UTM_MEDIUMS = Object.freeze({
  REFERRAL: "referral",
  AFFILIATE: "affiliate",
  CPC: "cpc",
  BANNER: "banner",
  EMAIL: "email",
  NEWSLETTER: "newsletter",
  SOCIAL: "social",
});

const UTM_CAMPAIGNS = Object.freeze({
  GENERAL: "general",
  LOCATION_DETAILS: "locationpage",
  PRODUCT_DETAILS: "productdetails",
  DELIVERY: "deliverypage",
  FILTER: "filterpage",
  MAP: "map",
  LEARN: "learnpage",
  ABOUT: "aboutpage",
  FAQ: "faqpage",
});

const APPCONFIG = {
  colors: {
    hamburger: {
      light: "white",
      dark: "black",
    }
  }
};

const STYLES = StyleSheet.create({
  flex1: {
    flex: 1,
  },
  flex1Center: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
  },
  image18x18: {
    width: 18,
    height: 18,
  },
  image24x24: {
    width: 24,
    height: 24,
  },
  image25x25: {
    width: 25,
    height: 25,
  },
  image30x30: {
    width: 30,
    height: 30,
  },
  margin10: {
    margin: 10,
  },
  marginBottom10: {
    marginBottom: 10,
  },
  marginBottom20: {
    marginBottom: 20,
  },
  marginVertical10: {
    marginVertical: 10,
  },
  marginVertical20: {
    marginVertical: 20,
  },
  marginTop5: {
    marginTop: 5,
  },
  marginTop10: {
    marginTop: 10,
  },
  marginTop15: {
    marginTop: 15,
  },
  marginTop20: {
    marginTop: 20,
  },
  padding20: {
    padding: 20,
  },
  paddingTop10Bottom15: {
    paddingTop: 10,
    paddingBottom: 15,
  },
  paddingVertical10: {
    paddingVertical: 10,
  },
  paddingVertical10Left10: {
    paddingVertical: 10,
    paddingLeft: 10,
  },
  paddingVertical10Right10: {
    paddingVertical: 10,
    paddingRight: 10,
  },
  paddingRight10: {
    paddingRight: 10,
  },
  fontSize17: {
    fontSize: 17,
  },
  fontBoldColorRed: {
    fontWeight: "bold",
    color: "red",
  },
  backgroundWhite: {
    backgroundColor: "#ffffff",
  },
  width100PmarginVertical10borderNone: {
    width: "100%",
    marginVertical: 10,
    borderColor: 'none',
  },
  width100PmarginVertical10borderNoneMinHeight: {
    width: "100%",
    marginVertical: 10,
    borderColor: 'none',
    ...Platform.select({
      ios: {
        minHeight: 20 * 5
      },
    })
  },
  width100PmarginVertical10border1: {
    width: "100%",
    marginVertical: 10,
    borderWidth: 1,
    padding: 10,
    borderColor: '#000000',
  },
  snackWrapper: {
    alignSelf: "center",
    zIndex: 1,
    width: 326,
  },
  link: {
    color: "#0000EE",
    textDecorationLine: "underline",
  },
});

const LOGIN_STATE = Object.freeze({
  LOGGED_OUT: 0,
  LOGGED_IN_LOCALLY: 1,
  LOGGED_IN_REMOTELY: 2,
});

const DEFAULT_LOGIN_STATE = LOGIN_STATE.LOGGED_IN_LOCALLY;

const DISPATCH = Object.freeze({
  RESET_STATE: "resetState",
  SET_INITIAL_DATA: "setInitialData",
  LIGHT_MODE: "lightMode",
  EXTRA_DEBUG: "extraDebug",
  SET_LAST_MAIN_VIEW: "setLastMainView",
  SET_DELIVERY_LOCATION_FILTER: "setDeliveryLocationFilter",
  SET_MAP_CENTER: "setMapCenter",
  MOVE_MAP: "moveMap",
  UPDATE_DATA_FILTER: "updateDataFilter",
  UPDATE_LAST_GPS_LOCATION: "updateLastGPSLocation",
});

const LOCAL_DATA_KEYS = Object.freeze({
  REMOTE_SESSION: "remoteSession",
  SAVED_APP_STATE: "savedAppState",
  DATA_FILTER: "dataFilter",
  USED_APP_BEFORE: "usedAppBefore",
  USED_QUICK_FILTER_BEFORE: "usedQuickFilterBefore",
  SEEN_SUBSCRIBE_POPUP: "seenSubscribePopup",
});

const SIMPLE_GLOBAL_STATE = Object.freeze({
  DEFAULT_SHOW_DEBUG: "defaultShowDebug",
  PRODUCER_SEARCH: "producerSearch",
  PRODUCT_SEARCH: "productSearch",
});

// https://developers.google.com/maps/documentation/javascript/overview
// https://developers.google.com/maps/documentation/javascript/react-map
// https://developers.google.com/maps/documentation/javascript/adding-a-google-map
// https://developers.google.com/maps/documentation/javascript/overview#MapOptions
// https://developers.google.com/maps/documentation/javascript/reference
// https://developers.google.com/maps/documentation/javascript/reference/map#Map
const ZOOM_LEVELS = Object.freeze({
  WORLD: 1,
  CONTINENT: 5,
  COUNTRY: 6,
  STATE: 7,
  CITY: 10,
  STREETS: 15,
  BUILDINGS: 20,
});

const DEFAULT_ZOOMED = ZOOM_LEVELS.CITY + 1;

// In the middle of the "United States" text of Google Maps
const CENTER_UNITED_STATES = {
  lat: 39.837917118760664,
  lng: -101.47794131398716,
};

// https://developers.google.com/maps/documentation/javascript/style-reference#style-features
// https://developers.google.com/maps/documentation/javascript/style-reference#style-elements
// https://developers.google.com/maps/documentation/javascript/style-reference#stylers
const MAP_FEATURES = [
  {
    featureType: "poi",
    elementType: "labels",
    stylers: [{ visibility: "off" }],
  },
  {
    featureType: "transit",
    elementType: "labels",
    stylers: [{ visibility: "off" }],
  },
  {
    featureType: "administrative.neighborhood",
    elementType: "labels",
    stylers: [{ visibility: "off" }],
  },
  /*
  // This removes suburb names but also removes city names:
  {
    featureType: "administrative.locality",
    elementType: "labels",
    stylers: [{ visibility: "off" }],
  },
  */
];

// When adding here, also add to SCREEN_LINKS
const SCREENS = Object.freeze({
  HOME: "Home",
  MAP_STACK: "MapStack",
  MAP: "Map",
  SUGGEST_STACK: "SuggestStack",
  MORE_STACK: "MoreStack",
  LOCATION_DETAILS: "LocationDetails",
  LOCATION_ONLINE_DETAILS: "LocationOnlineDetails",
  PRODUCER_DETAILS: "ProducerDetails",
  PRODUCER_ONLINE_DETAILS: "ProducerOnlineDetails",
  PRODUCER_EXPLORE_DETAILS: "ProducerExploreDetails",
  LOCATION_LIST_DETAILS: "LocationListDetails",
  PRODUCT_DETAILS: "Product Details",
  PRODUCT_ONLINE_DETAILS: "ProductOnlineDetails",
  PRODUCT_EXPLORE_DETAILS: "ProductExploreDetails",
  FILTER: "Filter",
  FILTER_MAP: "FilterMap",
  ADD_LOCATION: "AddLocation",
  MORE: "More",
  FAQ: "FAQ",
  MORE_CONTACT: "MoreContact",
  MORE_ABOUT: "MoreAbout",
  MORE_PRIVACY: "MorePrivacy",
  MORE_POLICIES: "MorePolicies",
  MORE_TERMS: "MoreTerms",
  MORE_SUGGEST_LOCATION: "MoreSuggestLocation",
  MORE_LIST_LOCATIONS_NEAR: "MoreListLocationsNear",
  MORE_DEBUG: "MoreDebug",
  MORE_NEWS: "MoreNews",
  MORE_TEST: "MoreTest",
  MORE_SUBSCRIBE: "MoreSubscribe",
  LEARN: "Learn",
  CREDITS: "Credits",
  DELIVERY: "Delivery",
  DELIVERY_STACK: "DeliveryStack",
  COARSE_FILTER: "Filter",
  ALL_PRODUCTS: "AllProducts",
  ALL_PRODUCERS: "AllProducers",
  PRODUCTS_ALL_DETAILS: "AllProductDetails",
  EXPLORE: "Explore",
  EXPLORE_STACK: "ExploreStack",
  SEARCH: "Search",
});

const SCREEN_LINKS = Object.freeze({
  [SCREENS.HOME]: "map",
  [SCREENS.MAP]: "map",
  [SCREENS.MAP_STACK]: "map",
  [SCREENS.LOCATION_DETAILS]: "place/:locationid",
  [SCREENS.PRODUCER_DETAILS]: "producer/:producer",
  [SCREENS.PRODUCT_DETAILS]: "product/:productid",
  [SCREENS.DELIVERY]: "delivery",
  [SCREENS.DELIVERY_STACK]: "delivery",
  [SCREENS.LOCATION_ONLINE_DETAILS]: "place_delivery/:locationid/:online",
  [SCREENS.PRODUCER_ONLINE_DETAILS]: "producer_delivery/:producer/:online",
  [SCREENS.LOCATION_LIST_DETAILS]: "place_list/:locationid",
  [SCREENS.PRODUCT_ONLINE_DETAILS]: "product_delivery/:productid/:online",
  [SCREENS.PRODUCT_EXPLORE_DETAILS]: "product_explore/:productid/:explore",
  [SCREENS.PRODUCER_EXPLORE_DETAILS]: "producer_explore/:producer/:explore",
  [SCREENS.FILTER]: "filter",
  [SCREENS.FILTER_MAP]: "quickfilter",
  [SCREENS.ADD_LOCATION]: "add",
  [SCREENS.FAQ]: "faq",
  [SCREENS.MORE_STACK]: "more",
  [SCREENS.MORE]: "more",
  [SCREENS.MORE_NEWS]: "news",
  [SCREENS.MORE_TEST]: "test",
  [SCREENS.LEARN]: "learn",
  [SCREENS.CREDITS]: "credits",
  [SCREENS.MORE_CONTACT]: "contact",
  [SCREENS.MORE_SUBSCRIBE]: "subscribe",
  [SCREENS.MORE_ABOUT]: "about",
  [SCREENS.MORE_PRIVACY]: "privacy",
  [SCREENS.MORE_POLICIES]: "policies",
  [SCREENS.MORE_TERMS]: "terms",
  [SCREENS.SUGGEST_STACK]: "suggest",
  [SCREENS.MORE_SUGGEST_LOCATION]: "suggest",
  [SCREENS.MORE_LIST_LOCATIONS_NEAR]: "listnear",
  [SCREENS.MORE_DEBUG]: "debug",
  [SCREENS.ALL_PRODUCTS]: "allproducts",
  [SCREENS.ALL_PRODUCERS]: "allproducers",
  [SCREENS.PRODUCTS_ALL_DETAILS]: "allproducts/:productid",
  [SCREENS.EXPLORE]: "explore",
  [SCREENS.EXPLORE_STACK]: "explore",
  [SCREENS.SEARCH]: "search",
});

const SCREEN_TITLES = Object.freeze({
  [SCREENS.HOME]: "Home",
  [SCREENS.MAP]: "Map",
  [SCREENS.MAP_STACK]: "Map",
  [SCREENS.DELIVERY]: "Delivery",
  [SCREENS.DELIVERY_STACK]: "Delivery",
  [SCREENS.FILTER]: "Filter",
  [SCREENS.FILTER_MAP]: "Filter",
  [SCREENS.ADD_LOCATION]: "Add Location",
  [SCREENS.FAQ]: "FAQ",
  [SCREENS.MORE_STACK]: "More Links",
  [SCREENS.MORE]: "More Links",
  [SCREENS.MORE_NEWS]: "News",
  [SCREENS.MORE_TEST]: "Test",
  [SCREENS.LEARN]: "Learn More",
  [SCREENS.CREDITS]: "Credits",
  [SCREENS.MORE_CONTACT]: "Contact Us",
  [SCREENS.MORE_SUBSCRIBE]: "Subscribe",
  [SCREENS.MORE_ABOUT]: "About",
  [SCREENS.MORE_PRIVACY]: "Privacy",
  [SCREENS.MORE_POLICIES]: "Policies",
  [SCREENS.MORE_TERMS]: "Terms & Conditions",
  [SCREENS.SUGGEST_STACK]: "Suggest New Location",
  [SCREENS.MORE_SUGGEST_LOCATION]: "Suggest New Location",
  [SCREENS.MORE_DEBUG]: "Debug",
  [SCREENS.ALL_PRODUCTS]: "All Products",
  [SCREENS.ALL_PRODUCERS]: "All Producers",
  [SCREENS.EXPLORE]: "Explore",
  [SCREENS.EXPLORE_STACK]: "Explore",
  [SCREENS.SEARCH]: "Search",
});

// https://callstack.github.io/react-native-paper/theming.html
const REACT_NATIVE_PAPER_THEME = {
  ...PaperDefaultTheme,
  roundness: 2,
  colors: {
    ...PaperDefaultTheme.colors,
    primary: '#0641a0',
  },
};

const CERTIFICATION_NAMES = Object.freeze({
  ANIMAL_WELFARE_APPROVED: "Animal Welfare Approved by AGW",
  CERTIFIED_HUMANE: "Certified Humane®",
  APPLEGATE_HUMANELY_RAISED: "Applegate Humanely Raised®",
  GAP: "Global Animal Partnership®",
  GAP_FISH: "Global Animal Partnership® Salmon Welfare",
  AGW_CERTIFIED_GRASSFED: "Certified Grassfed by AGW",
  AGW_CERTIFIED_NONGMO: "Certified Non-GMO by AGW",
  AGW_CERTIFIED_ORGANIC: "Certified Organic by AGW",
  AGW_CERTIFIED_REGENERATIVE: "Certified Regenerative by AGW",
  AGW_SALMON_WELFARE_CERTIFIED: "Salmon Welfare Certified by AGW",
  AMERICAN_HUMANE_CERTIFIED: "American Humane Certified™",
  AMERICAN_GRASSFED_ASSOCIATION: "American Grassfed Association®",
  USDA_ORGANIC: "USDA Organic",
  FOOD_ALLIANCE_CERTIFIED: "Food Alliance Certified™",
  ONE_HEALTH_CERTIFIED: "One Health Certified",
  SELF_CERTIFIED: "Self-Certified Humanely Raised",
  REGENERATIVE_ORGANIC_CERTIFIED: "Regenerative Organic Certified®",
  BETTER_CHICKEN_PROJECT: "Better Chicken Project",
  CERTIFIED_HUMANE_APPROVED_HATCHERIES: "Certified Humane Approved Hatcheries™",
});

const ASPCA_RECOMMENDED_SUFFIX = "";
//const ASPCA_RECOMMENDED_SUFFIX = " (ASPCA-Recommended)";

const TYPE_KEYS = Object.freeze({
  TITLE: "title",
  DATABASE_KEY: "dbkey",
  URL_KEY: "urlkey",
});

// The order of elements controls the order in the Search pane
const PRODUCT_TYPES = Object.freeze({
  CHICKEN: { [TYPE_KEYS.URL_KEY]: "ch", [TYPE_KEYS.TITLE]: "Chicken", [TYPE_KEYS.DATABASE_KEY]: "chicken" },
  CHICKEN_EGGS: { [TYPE_KEYS.URL_KEY]: "che", [TYPE_KEYS.TITLE]: "Eggs (Chicken)", [TYPE_KEYS.DATABASE_KEY]: "eggs", },
  COW: { [TYPE_KEYS.URL_KEY]: "c", [TYPE_KEYS.TITLE]: "Cow", [TYPE_KEYS.DATABASE_KEY]: "beef", },
  VEAL: { [TYPE_KEYS.URL_KEY]: "vl", [TYPE_KEYS.TITLE]: "Veal", [TYPE_KEYS.DATABASE_KEY]: "veal", },
  GOAT: { [TYPE_KEYS.URL_KEY]: "g", [TYPE_KEYS.TITLE]: "Goat", [TYPE_KEYS.DATABASE_KEY]: "goat", },
  LAMB: { [TYPE_KEYS.URL_KEY]: "lb", [TYPE_KEYS.TITLE]: "Lamb", [TYPE_KEYS.DATABASE_KEY]: "lamb", },
  MUTTON: { [TYPE_KEYS.URL_KEY]: "m", [TYPE_KEYS.TITLE]: "Mutton", [TYPE_KEYS.DATABASE_KEY]: "mutton", },
  COW_DAIRY: { [TYPE_KEYS.URL_KEY]: "cd", [TYPE_KEYS.TITLE]: "Dairy (Cow Milk)", [TYPE_KEYS.DATABASE_KEY]: "dairy", },
  GOAT_DAIRY: { [TYPE_KEYS.URL_KEY]: "gd", [TYPE_KEYS.TITLE]: "Dairy (Goat Milk)", [TYPE_KEYS.DATABASE_KEY]: "dairygoat", },
  SHEEP_DAIRY: { [TYPE_KEYS.URL_KEY]: "sd", [TYPE_KEYS.TITLE]: "Dairy (Sheep Milk)", [TYPE_KEYS.DATABASE_KEY]: "dairysheep", },
  COW_CHEESE: { [TYPE_KEYS.TITLE]: "Cheese (Cow Milk)", [TYPE_KEYS.URL_KEY]: "cc", [TYPE_KEYS.DATABASE_KEY]: "cheese" },
  GOAT_CHEESE: { [TYPE_KEYS.URL_KEY]: "gc", [TYPE_KEYS.TITLE]: "Cheese (Goat Milk)", [TYPE_KEYS.DATABASE_KEY]: "cheesegoat", },
  SHEEP_CHEESE: { [TYPE_KEYS.URL_KEY]: "sc", [TYPE_KEYS.TITLE]: "Cheese (Sheep Milk)", [TYPE_KEYS.DATABASE_KEY]: "cheesesheep", },
  PORK: { [TYPE_KEYS.URL_KEY]: "po", [TYPE_KEYS.TITLE]: "Pork", [TYPE_KEYS.DATABASE_KEY]: "pork", },
  BISON: { [TYPE_KEYS.URL_KEY]: "b", [TYPE_KEYS.TITLE]: "Bison", [TYPE_KEYS.DATABASE_KEY]: "bison", },
  TURKEY: { [TYPE_KEYS.URL_KEY]: "t", [TYPE_KEYS.TITLE]: "Turkey", [TYPE_KEYS.DATABASE_KEY]: "turkey", },
  TURKEY_EGGS: { [TYPE_KEYS.URL_KEY]: "te", [TYPE_KEYS.TITLE]: "Eggs (Turkey)", [TYPE_KEYS.DATABASE_KEY]: "eggs_turkey", },
  DUCK: { [TYPE_KEYS.URL_KEY]: "d", [TYPE_KEYS.TITLE]: "Duck", [TYPE_KEYS.DATABASE_KEY]: "duck", },
  DUCK_EGGS: { [TYPE_KEYS.URL_KEY]: "de", [TYPE_KEYS.TITLE]: "Eggs (Duck)", [TYPE_KEYS.DATABASE_KEY]: "eggs_duck", },
  VENISON: { [TYPE_KEYS.URL_KEY]: "v", [TYPE_KEYS.TITLE]: "Venison", [TYPE_KEYS.DATABASE_KEY]: "venison", },
  FOR_CATS: { [TYPE_KEYS.URL_KEY]: "cf", [TYPE_KEYS.TITLE]: "Cat food", [TYPE_KEYS.DATABASE_KEY]: "catfood", },
  FOR_DOGS: { [TYPE_KEYS.URL_KEY]: "df", [TYPE_KEYS.TITLE]: "Dog food", [TYPE_KEYS.DATABASE_KEY]: "dogfood", },
  //FISH: { [TYPE_KEYS.URL_KEY]: "f", [TYPE_KEYS.TITLE]: "Fish", [TYPE_KEYS.DATABASE_KEY]: "fish", },
  PANTRY: { [TYPE_KEYS.URL_KEY]: "pa", [TYPE_KEYS.TITLE]: "Pantry Foods", [TYPE_KEYS.DATABASE_KEY]: "pantry", },
  SWEETS: { [TYPE_KEYS.URL_KEY]: "sw", [TYPE_KEYS.TITLE]: "Sweets & Baked Goods", [TYPE_KEYS.DATABASE_KEY]: "sweet", },
  BATH_BODY: { [TYPE_KEYS.URL_KEY]: "bb", [TYPE_KEYS.TITLE]: "Bath, Body & Skin Care", [TYPE_KEYS.DATABASE_KEY]: "bath_body", },
  LEATHER: { [TYPE_KEYS.URL_KEY]: "le", [TYPE_KEYS.TITLE]: "Leather", [TYPE_KEYS.DATABASE_KEY]: "leather", },
  ACCESSORIES: { [TYPE_KEYS.URL_KEY]: "a", [TYPE_KEYS.TITLE]: "Accessories", [TYPE_KEYS.DATABASE_KEY]: "accessories", },
  CLOTHING: { [TYPE_KEYS.URL_KEY]: "ci", [TYPE_KEYS.TITLE]: "Clothing", [TYPE_KEYS.DATABASE_KEY]: "clothing", },
  HOME_GOODS: { [TYPE_KEYS.URL_KEY]: "hg", [TYPE_KEYS.TITLE]: "Home Goods", [TYPE_KEYS.DATABASE_KEY]: "homegoods", },
  //FARM_STAY: { [TYPE_KEYS.URL_KEY]: "fs", [TYPE_KEYS.TITLE]: "Overnight Farmstay", [TYPE_KEYS.DATABASE_KEY]: "farmstay", },
});

const SYNTHESIZED_TYPES = Object.freeze({
  FOR_HUMANS: { [TYPE_KEYS.URL_KEY]: "h", [TYPE_KEYS.TITLE]: "For People", [TYPE_KEYS.DATABASE_KEY]: "for_humans", },
  FOR_PETS: { [TYPE_KEYS.URL_KEY]: "p", [TYPE_KEYS.TITLE]: "For Pets", [TYPE_KEYS.DATABASE_KEY]: "for_pets", },
  GENERAL_SEARCH: { [TYPE_KEYS.URL_KEY]: "q", [TYPE_KEYS.TITLE]: "Search", [TYPE_KEYS.DATABASE_KEY]: "generalsearch", },
  LOCATION_ID: { [TYPE_KEYS.URL_KEY]: "l", [TYPE_KEYS.TITLE]: "Location", [TYPE_KEYS.DATABASE_KEY]: "l", },
  PRODUCER: { [TYPE_KEYS.URL_KEY]: "pr", [TYPE_KEYS.TITLE]: "Producer", [TYPE_KEYS.DATABASE_KEY]: "pr", },
  PRODUCT: { [TYPE_KEYS.URL_KEY]: "pt", [TYPE_KEYS.TITLE]: "Product", [TYPE_KEYS.DATABASE_KEY]: "pt", },
  QUERY_ID: { [TYPE_KEYS.URL_KEY]: "qid", [TYPE_KEYS.TITLE]: "Query", [TYPE_KEYS.DATABASE_KEY]: "qid", },
});

const GROUP_TYPES = Object.freeze({
  PASTURE: { [TYPE_KEYS.URL_KEY]: "pasture", [TYPE_KEYS.TITLE]: "Pasture", [TYPE_KEYS.DATABASE_KEY]: "pasture", },
  OUTDOORS: { [TYPE_KEYS.URL_KEY]: "outdoorsplus", [TYPE_KEYS.TITLE]: "Outdoors+", [TYPE_KEYS.DATABASE_KEY]: "outdoorsplus", },
  INDOORS: { [TYPE_KEYS.URL_KEY]: "indoorsplus", [TYPE_KEYS.TITLE]: "Indoors+", [TYPE_KEYS.DATABASE_KEY]: "indoorsplus", },
});

const WELFARE_CERTIFICATIONS = Object.freeze({
  ANIMAL_WELFARE_APPROVED: { [TYPE_KEYS.URL_KEY]: "aw", [TYPE_KEYS.TITLE]: "Animal Welfare Approved by AGW", [TYPE_KEYS.DATABASE_KEY]: "animal_welfare_approved", },
  CERTIFIED_REGENERATIVE: { [TYPE_KEYS.URL_KEY]: "cw", [TYPE_KEYS.TITLE]: "Certified Regenerative by AGW", [TYPE_KEYS.DATABASE_KEY]: "agw_certified_regenerative", },
  CERTIFIED_GRASSFED: { [TYPE_KEYS.URL_KEY]: "cs", [TYPE_KEYS.TITLE]: "Certified Grassfed by AGW", [TYPE_KEYS.DATABASE_KEY]: "agw_certified_grassfed", },
  CERTIFIED_NON_GMO: { [TYPE_KEYS.URL_KEY]: "cm", [TYPE_KEYS.TITLE]: "Certified Non-GMO by AGW", [TYPE_KEYS.DATABASE_KEY]: "agw_certified_non_gmo", },
  CERTIFIED_ORGANIC: { [TYPE_KEYS.URL_KEY]: "co", [TYPE_KEYS.TITLE]: "Certified Organic by AGW", [TYPE_KEYS.DATABASE_KEY]: "agw_certified_organic", },
  REGENERATIVE_ORGANIC_CERTIFIED: { [TYPE_KEYS.URL_KEY]: "ro", [TYPE_KEYS.TITLE]: "Regenerative Organic Certified®", [TYPE_KEYS.DATABASE_KEY]: "regenerative_organic_certified", },
  CERTIFIED_HUMANE: { [TYPE_KEYS.URL_KEY]: "cu", [TYPE_KEYS.TITLE]: "Certified Humane®", [TYPE_KEYS.DATABASE_KEY]: "certified_humane", },
  CERTIFIED_HUMANE_FREE_RANGE: { [TYPE_KEYS.URL_KEY]: "hr", [TYPE_KEYS.TITLE]: "Certified Humane® + Free range", [TYPE_KEYS.DATABASE_KEY]: "certified_humane_free_range", },
  CERTIFIED_HUMANE_GRASS_FED: { [TYPE_KEYS.URL_KEY]: "ca", [TYPE_KEYS.TITLE]: "Certified Humane® + Grass-fed", [TYPE_KEYS.DATABASE_KEY]: "certified_humane_grass_fed", },
  CERTIFIED_HUMANE_PASTURE_RAISED: { [TYPE_KEYS.URL_KEY]: "hp", [TYPE_KEYS.TITLE]: "Certified Humane® + Pasture Raised", [TYPE_KEYS.DATABASE_KEY]: "certified_humane_pasture_raised_grass_fed", },
  CERTIFIED_HUMANE_APPROVED_HATCHERIES: { [TYPE_KEYS.URL_KEY]: "hc", [TYPE_KEYS.TITLE]: "Certified Humane® Approved Hatcheries™", [TYPE_KEYS.DATABASE_KEY]: "certified_humane_approved_hatcheries", },
  //ANIMAL_WELFARE_CERTIFIED_1: { [TYPE_KEYS.URL_KEY]: "a1", [TYPE_KEYS.TITLE]: "Global Animal Partnership® Step 1", [TYPE_KEYS.DATABASE_KEY]: "animal_welfare_certified_1", },
  ANIMAL_WELFARE_CERTIFIED_2: { [TYPE_KEYS.URL_KEY]: "a2", [TYPE_KEYS.TITLE]: "Global Animal Partnership® Step 2", [TYPE_KEYS.DATABASE_KEY]: "animal_welfare_certified_2", },
  ANIMAL_WELFARE_CERTIFIED_3: { [TYPE_KEYS.URL_KEY]: "a3", [TYPE_KEYS.TITLE]: "Global Animal Partnership® Step 3", [TYPE_KEYS.DATABASE_KEY]: "animal_welfare_certified_3", },
  ANIMAL_WELFARE_CERTIFIED_4: { [TYPE_KEYS.URL_KEY]: "a4", [TYPE_KEYS.TITLE]: "Global Animal Partnership® Step 4", [TYPE_KEYS.DATABASE_KEY]: "animal_welfare_certified_4", },
  ANIMAL_WELFARE_CERTIFIED_5: { [TYPE_KEYS.URL_KEY]: "a5", [TYPE_KEYS.TITLE]: "Global Animal Partnership® Step 5", [TYPE_KEYS.DATABASE_KEY]: "animal_welfare_certified_5", },
  ANIMAL_WELFARE_CERTIFIED_6: { [TYPE_KEYS.URL_KEY]: "a6", [TYPE_KEYS.TITLE]: "Global Animal Partnership® Step 5+", [TYPE_KEYS.DATABASE_KEY]: "animal_welfare_certified_6", },
  BETTER_CHICKEN_PROJECT: { [TYPE_KEYS.URL_KEY]: "bc", [TYPE_KEYS.TITLE]: "Better Chicken Project", [TYPE_KEYS.DATABASE_KEY]: "better_chicken_project", },
  //APPLEGATE_HUMANELY_RAISED: { [TYPE_KEYS.URL_KEY]: "ah", [TYPE_KEYS.TITLE]: "Applegate Humanely Raised", [TYPE_KEYS.DATABASE_KEY]: "applegate_humanely_raised", },
  AMERICAN_GRASSFED_ASSOCIATION: { [TYPE_KEYS.URL_KEY]: "ag", [TYPE_KEYS.TITLE]: "American Grassfed Association", [TYPE_KEYS.DATABASE_KEY]: "american_grassfed_association", },
  USDA_ORGANIC: { [TYPE_KEYS.URL_KEY]: "o", [TYPE_KEYS.TITLE]: "USDA Organic", [TYPE_KEYS.DATABASE_KEY]: "usda_organic", },
  //ANIMAL_WELFARE_CERTIFIED_FISH_1: { [TYPE_KEYS.URL_KEY]: "s1", [TYPE_KEYS.TITLE]: "Global Animal Partnership® Salmon Welfare Step 1", [TYPE_KEYS.DATABASE_KEY]: "animal_welfare_certified_fish_1", },
  //ANIMAL_WELFARE_CERTIFIED_FISH_3: { [TYPE_KEYS.URL_KEY]: "s3", [TYPE_KEYS.TITLE]: "Global Animal Partnership® Salmon Welfare Step 3", [TYPE_KEYS.DATABASE_KEY]: "animal_welfare_certified_fish_3", },
  //ANIMAL_WELFARE_CERTIFIED_FISH_6: { [TYPE_KEYS.URL_KEY]: "s6", [TYPE_KEYS.TITLE]: "Global Animal Partnership® Salmon Welfare Step 5+", [TYPE_KEYS.DATABASE_KEY]: "animal_welfare_certified_fish_6", },
  //AGW_SALMON_WELFARE_CERTIFIED: { [TYPE_KEYS.URL_KEY]: "as", [TYPE_KEYS.TITLE]: "Salmon Welfare Certified by AGW", [TYPE_KEYS.DATABASE_KEY]: "agw_salmon_welfare_certified", },
});

const EXTRA_ANIMAL_WELFARE = Object.freeze({
  REGENERATIVE_FARM: { [TYPE_KEYS.URL_KEY]: "r", [TYPE_KEYS.TITLE]: "Regenerative Farm", [TYPE_KEYS.DATABASE_KEY]: "restorativefarms", },
  SLAUGHTERED_ON_FARM: { [TYPE_KEYS.URL_KEY]: "sl", [TYPE_KEYS.TITLE]: "Slaughtered On Farm", [TYPE_KEYS.DATABASE_KEY]: "slaughtered_without_transport", },
  NO_HORMONES: { [TYPE_KEYS.URL_KEY]: "nh", [TYPE_KEYS.TITLE]: "No Hormones", [TYPE_KEYS.DATABASE_KEY]: "nohormones", },
  NO_ANTIBIOTICS: { [TYPE_KEYS.URL_KEY]: "na", [TYPE_KEYS.TITLE]: "No Antibiotics", [TYPE_KEYS.DATABASE_KEY]: "noantibiotics", },
  PASTURE_RAISED: { [TYPE_KEYS.URL_KEY]: "pd", [TYPE_KEYS.TITLE]: "Pasture Raised", [TYPE_KEYS.DATABASE_KEY]: "pastureraised", },
  GRASS_FED: { [TYPE_KEYS.URL_KEY]: "gf", [TYPE_KEYS.TITLE]: "Grass-fed", [TYPE_KEYS.DATABASE_KEY]: "grassfed", },
  GRASS_FINISHED: { [TYPE_KEYS.URL_KEY]: "gs", [TYPE_KEYS.TITLE]: "Grass-finished", [TYPE_KEYS.DATABASE_KEY]: "grassfinished", },
  VEGETARIAN_DIET: { [TYPE_KEYS.URL_KEY]: "vd", [TYPE_KEYS.TITLE]: "Vegetarian diet", [TYPE_KEYS.DATABASE_KEY]: "vegetariandiet", },
  FREE_RANGE: { [TYPE_KEYS.URL_KEY]: "fg", [TYPE_KEYS.TITLE]: "Free-range", [TYPE_KEYS.DATABASE_KEY]: "freerange", },
  NO_PESTICIDES: { [TYPE_KEYS.URL_KEY]: "np", [TYPE_KEYS.TITLE]: "No Pesticides", [TYPE_KEYS.DATABASE_KEY]: "nopesticides", },
  SMALL_FAMILY_FARMS: { [TYPE_KEYS.URL_KEY]: "sf", [TYPE_KEYS.TITLE]: "Small family farms", [TYPE_KEYS.DATABASE_KEY]: "smallfamilyfarms", },
  CAGE_FREE: { [TYPE_KEYS.URL_KEY]: "nf", [TYPE_KEYS.TITLE]: "Cage-free", [TYPE_KEYS.DATABASE_KEY]: "cagefree", },
  B_CORPORATION: { [TYPE_KEYS.URL_KEY]: "bn", [TYPE_KEYS.TITLE]: "Certified B Corporation", [TYPE_KEYS.DATABASE_KEY]: "bcorporation", },
  NO_CRATES: { [TYPE_KEYS.URL_KEY]: "nt", [TYPE_KEYS.TITLE]: "No Crates", [TYPE_KEYS.DATABASE_KEY]: "nocrates", },
  NO_MALE_CULLING: { [TYPE_KEYS.URL_KEY]: "nu", [TYPE_KEYS.TITLE]: "No Male Culling", [TYPE_KEYS.DATABASE_KEY]: "nomaleculling", },
  CERTIFIED_CARBONNEUTRAL: { [TYPE_KEYS.URL_KEY]: "cn", [TYPE_KEYS.TITLE]: "Certified CarbonNeutral®", [TYPE_KEYS.DATABASE_KEY]: "carbonneutralcertified", },
  LIVE_WEBCAM: { [TYPE_KEYS.URL_KEY]: "lw", [TYPE_KEYS.TITLE]: "Live Webcam", [TYPE_KEYS.DATABASE_KEY]: "livewebcam", },
  NO_BEAK_TRIMMING: { [TYPE_KEYS.URL_KEY]: "nb", [TYPE_KEYS.TITLE]: "No Beak Trimming", [TYPE_KEYS.DATABASE_KEY]: "nobeaktrimming", },
  CONSISTENT_DAYLIGHT: { [TYPE_KEYS.URL_KEY]: "dy", [TYPE_KEYS.TITLE]: "Consistent daylight access", [TYPE_KEYS.DATABASE_KEY]: "consistentdaylight", },
  UPCYCLED_FEED: { [TYPE_KEYS.URL_KEY]: "up", [TYPE_KEYS.TITLE]: "Up-cycled Feed", [TYPE_KEYS.DATABASE_KEY]: "upcycledfeed", },
  USDA_GRASSFED: { [TYPE_KEYS.URL_KEY]: "ug", [TYPE_KEYS.TITLE]: "USDA Certified Grassfed", [TYPE_KEYS.DATABASE_KEY]: "usda_certified_grassfed", },
  UNVACCINATED: { [TYPE_KEYS.URL_KEY]: "u", [TYPE_KEYS.TITLE]: "Unvaccinated", [TYPE_KEYS.DATABASE_KEY]: "unvaccinated", },
  WEANED_NATURALLY: { [TYPE_KEYS.URL_KEY]: "wn", [TYPE_KEYS.TITLE]: "Weaned naturally", [TYPE_KEYS.DATABASE_KEY]: "weaned_naturally", },
  HUMANELY_HATCHED: { [TYPE_KEYS.URL_KEY]: "hh", [TYPE_KEYS.TITLE]: "Humanely Hatched®", [TYPE_KEYS.DATABASE_KEY]: "humanely_hatched", },
});

// for i in $(awk '/INGREDIENTS =/ {go=1;} $0 == "});" {go=0;} go && /\[TYPE_KEYS.DATABASE_KEY\]:/ {gsub(/[",]/, ""); print $(NF-1);}' js/context/AppConstants.js); do echo "$i;"; echo "select count(*) from find_humane_humane_location_humane_products a inner join find_humane_humane_products b on a.find_humane_humane_product_id = b.id where b.$i is true;"; done
const INGREDIENTS = Object.freeze({
  //PARTIALLY_HUMANE: { [TYPE_KEYS.URL_KEY]: "ph", [TYPE_KEYS.TITLE]: "", [TYPE_KEYS.DATABASE_KEY]: "partiallyhumane", },
  KOSHER: { [TYPE_KEYS.URL_KEY]: "k", [TYPE_KEYS.TITLE]: "Kosher", [TYPE_KEYS.DATABASE_KEY]: "kosher", },
  HALAL: { [TYPE_KEYS.URL_KEY]: "ha", [TYPE_KEYS.TITLE]: "Halal", [TYPE_KEYS.DATABASE_KEY]: "halal", },
  ZERO_SUGARS: { [TYPE_KEYS.URL_KEY]: "zs", [TYPE_KEYS.TITLE]: "Zero Sugars", [TYPE_KEYS.DATABASE_KEY]: "zerocarbs", },
  MICROWAVABLE: { [TYPE_KEYS.URL_KEY]: "mi", [TYPE_KEYS.TITLE]: "Microwavable", [TYPE_KEYS.DATABASE_KEY]: "microwavable", },
  FULLY_COOKED: { [TYPE_KEYS.URL_KEY]: "fc", [TYPE_KEYS.TITLE]: "Fully Cooked", [TYPE_KEYS.DATABASE_KEY]: "fullycooked", },
  FROZEN: { [TYPE_KEYS.URL_KEY]: "fr", [TYPE_KEYS.TITLE]: "Frozen", [TYPE_KEYS.DATABASE_KEY]: "frozen", },
  NO_CASEIN: { [TYPE_KEYS.URL_KEY]: "nc", [TYPE_KEYS.TITLE]: "No Casein", [TYPE_KEYS.DATABASE_KEY]: "nocasein", },
  GLUTEN_FREE: { [TYPE_KEYS.URL_KEY]: "ng", [TYPE_KEYS.TITLE]: "Gluten-free", [TYPE_KEYS.DATABASE_KEY]: "glutenfree", },
  NO_NITRATES: { [TYPE_KEYS.URL_KEY]: "nn", [TYPE_KEYS.TITLE]: "No nitrates", [TYPE_KEYS.DATABASE_KEY]: "nonitrates", },
  NO_SOY: { [TYPE_KEYS.URL_KEY]: "ny", [TYPE_KEYS.TITLE]: "No Soy", [TYPE_KEYS.DATABASE_KEY]: "nosoy", },
  NO_GMO: { [TYPE_KEYS.URL_KEY]: "nm", [TYPE_KEYS.TITLE]: "No GMOs", [TYPE_KEYS.DATABASE_KEY]: "nongmo", },
  NON_GMO_VERIFIED: { [TYPE_KEYS.URL_KEY]: "nv", [TYPE_KEYS.TITLE]: "Non-GMO Project Verified", [TYPE_KEYS.DATABASE_KEY]: "nongmoproject", },
  KETO_FRIENDLY: { [TYPE_KEYS.URL_KEY]: "kf", [TYPE_KEYS.TITLE]: "Keto-friendly", [TYPE_KEYS.DATABASE_KEY]: "ketofriendly", },
  PALEO_FRIENDLY: { [TYPE_KEYS.URL_KEY]: "pf", [TYPE_KEYS.TITLE]: "Paleo-friendly", [TYPE_KEYS.DATABASE_KEY]: "paleofriendly", },
  DAIRY_FREE: { [TYPE_KEYS.URL_KEY]: "da", [TYPE_KEYS.TITLE]: "Dairy-free", [TYPE_KEYS.DATABASE_KEY]: "dairyfree", },
  LOW_SODIUM: { [TYPE_KEYS.URL_KEY]: "ls", [TYPE_KEYS.TITLE]: "Low Sodium", [TYPE_KEYS.DATABASE_KEY]: "lowsodium", },
  //LOW_FAT: { [TYPE_KEYS.URL_KEY]: "lf", [TYPE_KEYS.TITLE]: "Low Fat", [TYPE_KEYS.DATABASE_KEY]: "lowfat", },
  NO_PRESERVATIVES: { [TYPE_KEYS.URL_KEY]: "ns", [TYPE_KEYS.TITLE]: "No Preservatives", [TYPE_KEYS.DATABASE_KEY]: "nopreservatives", },
  WHOLE30_APPROVED: { [TYPE_KEYS.URL_KEY]: "w", [TYPE_KEYS.TITLE]: "Whole30 Approved®", [TYPE_KEYS.DATABASE_KEY]: "whole30approved", },
  NO_ARTIFICIAL: { [TYPE_KEYS.URL_KEY]: "nl", [TYPE_KEYS.TITLE]: "No Artificial Ingredients", [TYPE_KEYS.DATABASE_KEY]: "noartificial", },
  CERTIFIED_PALEO: { [TYPE_KEYS.URL_KEY]: "cp", [TYPE_KEYS.TITLE]: "Certified Paleo®", [TYPE_KEYS.DATABASE_KEY]: "certified_paleo", },
  KETO_CERTIFIED: { [TYPE_KEYS.URL_KEY]: "kc", [TYPE_KEYS.TITLE]: "Keto Certified™", [TYPE_KEYS.DATABASE_KEY]: "keto_certified", },
  RECYCLABLE_PACKAGING: { [TYPE_KEYS.URL_KEY]: "rp", [TYPE_KEYS.TITLE]: "Recyclable Packaging", [TYPE_KEYS.DATABASE_KEY]: "recyclablepackaging", },
  NO_CORN: { [TYPE_KEYS.URL_KEY]: "nr", [TYPE_KEYS.TITLE]: "No Corn", [TYPE_KEYS.DATABASE_KEY]: "nocorn", },
});

// for i in $(awk '/LOCATION_FLAGS =/ {go=1;} $0 == "});" {go=0;} go && /\[TYPE_KEYS.DATABASE_KEY\]:/ {gsub(/[",]/, ""); print $(NF-1);}' js/context/AppConstants.js); do echo "$i;"; echo "select count(*) from find_humane_humane_location_humane_products a inner join find_humane_humane_locations b on a.find_humane_humane_location_id = b.id where b.$i IS TRUE;"; done
const LOCATION_FLAGS = Object.freeze({
  GROCERY_STORE: { [TYPE_KEYS.URL_KEY]: "go", [TYPE_KEYS.TITLE]: "Grocery store", [TYPE_KEYS.DATABASE_KEY]: "supermarket", },
  RESTAURANT: { [TYPE_KEYS.URL_KEY]: "re", [TYPE_KEYS.TITLE]: "Restaurant", [TYPE_KEYS.DATABASE_KEY]: "restaurant", },
  FARM: { [TYPE_KEYS.URL_KEY]: "fa", [TYPE_KEYS.TITLE]: "Farm", [TYPE_KEYS.DATABASE_KEY]: "farm", },
  FARM_STORE: { [TYPE_KEYS.URL_KEY]: "fm", [TYPE_KEYS.TITLE]: "Farm store", [TYPE_KEYS.DATABASE_KEY]: "farm_store", },
  FARMERS_MARKET: { [TYPE_KEYS.URL_KEY]: "ft", [TYPE_KEYS.TITLE]: "Farmers Market", [TYPE_KEYS.DATABASE_KEY]: "farmers_market", },
  CSA: { [TYPE_KEYS.URL_KEY]: "ct", [TYPE_KEYS.TITLE]: "Community-Supported Agriculture (CSA)", [TYPE_KEYS.DATABASE_KEY]: "csa", },
  FARM_STAY_LOCATION: { [TYPE_KEYS.URL_KEY]: "fb", [TYPE_KEYS.TITLE]: "Farm Stay & BnB", [TYPE_KEYS.DATABASE_KEY]: "farm_stay_bnb", },
  OFFERS_DELIVERY: { [TYPE_KEYS.URL_KEY]: "od", [TYPE_KEYS.TITLE]: "Offers delivery", [TYPE_KEYS.DATABASE_KEY]: "online_shopping", },
  CAFE: { [TYPE_KEYS.URL_KEY]: "ce", [TYPE_KEYS.TITLE]: "Café", [TYPE_KEYS.DATABASE_KEY]: "coffeeshop", },
  DINE_IN: { [TYPE_KEYS.URL_KEY]: "di", [TYPE_KEYS.TITLE]: "Dine-in", [TYPE_KEYS.DATABASE_KEY]: "dinein", },
  SOLAR_PANELS_USED: { [TYPE_KEYS.URL_KEY]: "sp", [TYPE_KEYS.TITLE]: "Solar panels used", [TYPE_KEYS.DATABASE_KEY]: "solarpanels", },
  BAKERY: { [TYPE_KEYS.URL_KEY]: "by", [TYPE_KEYS.TITLE]: "Bakery", [TYPE_KEYS.DATABASE_KEY]: "bakery", },
  SERVES_ALCOHOL: { [TYPE_KEYS.URL_KEY]: "sv", [TYPE_KEYS.TITLE]: "Serves Alcohol", [TYPE_KEYS.DATABASE_KEY]: "servesalcohol", },
  //FOOD_TRUCK: { [TYPE_KEYS.URL_KEY]: "fu", [TYPE_KEYS.TITLE]: "Food truck", [TYPE_KEYS.DATABASE_KEY]: "foodtruck", },
  ONLINE_ORDERING: { [TYPE_KEYS.URL_KEY]: "on", [TYPE_KEYS.TITLE]: "Offers online ordering", [TYPE_KEYS.DATABASE_KEY]: "website_ordering", },
  PRIVATE_EVENTS: { [TYPE_KEYS.URL_KEY]: "pe", [TYPE_KEYS.TITLE]: "Private events", [TYPE_KEYS.DATABASE_KEY]: "private_events", },
  WIFI: { [TYPE_KEYS.URL_KEY]: "wf", [TYPE_KEYS.TITLE]: "WiFi", [TYPE_KEYS.DATABASE_KEY]: "wifi", },
  WORK_ON_LAPTOP: { [TYPE_KEYS.URL_KEY]: "lp", [TYPE_KEYS.TITLE]: "Place to work on laptop", [TYPE_KEYS.DATABASE_KEY]: "workonlaptop", },
  BAR: { [TYPE_KEYS.URL_KEY]: "br", [TYPE_KEYS.TITLE]: "Bar", [TYPE_KEYS.DATABASE_KEY]: "bar", },
  //CLUB: { [TYPE_KEYS.URL_KEY]: "cl", [TYPE_KEYS.TITLE]: "Club", [TYPE_KEYS.DATABASE_KEY]: "club", },
  WINE_BAR: { [TYPE_KEYS.URL_KEY]: "wb", [TYPE_KEYS.TITLE]: "Wine bar", [TYPE_KEYS.DATABASE_KEY]: "winebar", },
  DISTILLERY: { [TYPE_KEYS.URL_KEY]: "dl", [TYPE_KEYS.TITLE]: "Distillery", [TYPE_KEYS.DATABASE_KEY]: "distillery", },
  COCKTAIL_BAR: { [TYPE_KEYS.URL_KEY]: "cb", [TYPE_KEYS.TITLE]: "Cocktail bar", [TYPE_KEYS.DATABASE_KEY]: "cocktailbar", },
  //SPEAKEASY: { [TYPE_KEYS.URL_KEY]: "sy", [TYPE_KEYS.TITLE]: "Speakeasy", [TYPE_KEYS.DATABASE_KEY]: "speakeasy", },
  //SPORTS_BAR: { [TYPE_KEYS.URL_KEY]: "sb", [TYPE_KEYS.TITLE]: "Sports bar", [TYPE_KEYS.DATABASE_KEY]: "sportsbar", },
  //DIVE_BAR: { [TYPE_KEYS.URL_KEY]: "db", [TYPE_KEYS.TITLE]: "Dive bar", [TYPE_KEYS.DATABASE_KEY]: "divebar", },
  //LIVE_MUSIC: { [TYPE_KEYS.URL_KEY]: "lm", [TYPE_KEYS.TITLE]: "Live music", [TYPE_KEYS.DATABASE_KEY]: "livemusic", },
  HOTEL: { [TYPE_KEYS.URL_KEY]: "ho", [TYPE_KEYS.TITLE]: "Hotel", [TYPE_KEYS.DATABASE_KEY]: "hotel", },
  //HOTEL_BAR: { [TYPE_KEYS.URL_KEY]: "hb", [TYPE_KEYS.TITLE]: "Hotel bar", [TYPE_KEYS.DATABASE_KEY]: "hotelbar", },
  //CIGAR_BAR: { [TYPE_KEYS.URL_KEY]: "cg", [TYPE_KEYS.TITLE]: "Cigar bar", [TYPE_KEYS.DATABASE_KEY]: "cigarbar", },
  //LOUNGE: { [TYPE_KEYS.URL_KEY]: "lu", [TYPE_KEYS.TITLE]: "Lounge", [TYPE_KEYS.DATABASE_KEY]: "lounge", },
  //ARCADE: { [TYPE_KEYS.URL_KEY]: "ar", [TYPE_KEYS.TITLE]: "Arcade", [TYPE_KEYS.DATABASE_KEY]: "arcade", },
  //COMEDY_CLUB: { [TYPE_KEYS.URL_KEY]: "cy", [TYPE_KEYS.TITLE]: "Comedy club", [TYPE_KEYS.DATABASE_KEY]: "comedyclub", },
  //KARAOKE_BAR: { [TYPE_KEYS.URL_KEY]: "kk", [TYPE_KEYS.TITLE]: "Karaoke bar", [TYPE_KEYS.DATABASE_KEY]: "karaokebar", },
  //TIKI_BAR: { [TYPE_KEYS.URL_KEY]: "tk", [TYPE_KEYS.TITLE]: "Tiki bar", [TYPE_KEYS.DATABASE_KEY]: "tikibar", },
  GIFT_SHOP: { [TYPE_KEYS.URL_KEY]: "gi", [TYPE_KEYS.TITLE]: "Gift shop", [TYPE_KEYS.DATABASE_KEY]: "giftshop", },
  HAIR_SALON: { [TYPE_KEYS.URL_KEY]: "hs", [TYPE_KEYS.TITLE]: "Hair salon", [TYPE_KEYS.DATABASE_KEY]: "hairsalon", },
  PICKUP_ONLINE_ORDERS: { [TYPE_KEYS.URL_KEY]: "pp", [TYPE_KEYS.TITLE]: "Pickup online orders", [TYPE_KEYS.DATABASE_KEY]: "localpickup", },
  ONLINE_ONLY: { [TYPE_KEYS.URL_KEY]: "oo", [TYPE_KEYS.TITLE]: "Online-only", [TYPE_KEYS.DATABASE_KEY]: "onlineonly", },
  BOUTIQUE: { [TYPE_KEYS.URL_KEY]: "bo", [TYPE_KEYS.TITLE]: "Boutique", [TYPE_KEYS.DATABASE_KEY]: "boutique", },
  SPA: { [TYPE_KEYS.URL_KEY]: "sa", [TYPE_KEYS.TITLE]: "Spa", [TYPE_KEYS.DATABASE_KEY]: "spa", },
  SUPPLEMENT_STORE: { [TYPE_KEYS.URL_KEY]: "su", [TYPE_KEYS.TITLE]: "Supplement store", [TYPE_KEYS.DATABASE_KEY]: "supplementstore", },
  GARDEN_CENTER: { [TYPE_KEYS.URL_KEY]: "ga", [TYPE_KEYS.TITLE]: "Garden center", [TYPE_KEYS.DATABASE_KEY]: "gardencenter", },
  HERB_SHOP: { [TYPE_KEYS.URL_KEY]: "he", [TYPE_KEYS.TITLE]: "Herb shop", [TYPE_KEYS.DATABASE_KEY]: "herbshop", },
  HOME_GOODS_STORE: { [TYPE_KEYS.URL_KEY]: "hd", [TYPE_KEYS.TITLE]: "Home goods store", [TYPE_KEYS.DATABASE_KEY]: "homegoodsstore", },
  CLOTHING_STORE: { [TYPE_KEYS.URL_KEY]: "cr", [TYPE_KEYS.TITLE]: "Clothing store", [TYPE_KEYS.DATABASE_KEY]: "clothingstore", },
  SCHOOL: { [TYPE_KEYS.URL_KEY]: "sh", [TYPE_KEYS.TITLE]: "School", [TYPE_KEYS.DATABASE_KEY]: "school", },
  NONPROFIT: { [TYPE_KEYS.URL_KEY]: "ni", [TYPE_KEYS.TITLE]: "Non-profit", [TYPE_KEYS.DATABASE_KEY]: "nonprofit", },
  WEDDINGS: { [TYPE_KEYS.URL_KEY]: "wd", [TYPE_KEYS.TITLE]: "Weddings", [TYPE_KEYS.DATABASE_KEY]: "weddings", },
  BUTCHER: { [TYPE_KEYS.URL_KEY]: "bu", [TYPE_KEYS.TITLE]: "Butcher", [TYPE_KEYS.DATABASE_KEY]: "butcher", },
  GENERAL_STORE: { [TYPE_KEYS.URL_KEY]: "ge", [TYPE_KEYS.TITLE]: "General store", [TYPE_KEYS.DATABASE_KEY]: "generalstore", },
});

const DATA_FILTERS_BUILTIN_NAMES = Object.freeze({
  INDOORS: "Indoors with Enrichments" + ASPCA_RECOMMENDED_SUFFIX,
  OUTDOORS: "Default: Outdoor Access" + ASPCA_RECOMMENDED_SUFFIX,
  PASTURE: "Pasture" + ASPCA_RECOMMENDED_SUFFIX,
});

const DATA_FILTERS_BUILTIN_DEFAULT = DATA_FILTERS_BUILTIN_NAMES.OUTDOORS;

function getBuiltinOptionsIndoorsPlus() {
  return {
    [GROUP_TYPES.INDOORS[TYPE_KEYS.URL_KEY]]: "y",
  };
}

function getBuiltinOptionsOutdoorsPlus() {
  return {
    [GROUP_TYPES.OUTDOORS[TYPE_KEYS.URL_KEY]]: "y",
  };
}

function getBuiltinOptionsPasture() {
  return {
    [GROUP_TYPES.PASTURE[TYPE_KEYS.URL_KEY]]: "y",
  };
}

function clearBuiltinOptions(filter) {
  delete filter[GROUP_TYPES.INDOORS[TYPE_KEYS.URL_KEY]];
  delete filter[GROUP_TYPES.OUTDOORS[TYPE_KEYS.URL_KEY]];
  delete filter[GROUP_TYPES.PASTURE[TYPE_KEYS.URL_KEY]];
  return filter;
}

const DATA_FILTERS_BUILTIN_OPTIONS = Object.freeze({
  [DATA_FILTERS_BUILTIN_NAMES.INDOORS]: getBuiltinOptionsIndoorsPlus(),
  [DATA_FILTERS_BUILTIN_NAMES.OUTDOORS]: getBuiltinOptionsOutdoorsPlus(),
  [DATA_FILTERS_BUILTIN_NAMES.PASTURE]: getBuiltinOptionsPasture(),
});

const DATA_FILTERS_BUILTIN_REVERSE_MAP = Object.freeze({
  [GROUP_TYPES.INDOORS[TYPE_KEYS.URL_KEY]]: DATA_FILTERS_BUILTIN_NAMES.INDOORS,
  [GROUP_TYPES.OUTDOORS[TYPE_KEYS.URL_KEY]]: DATA_FILTERS_BUILTIN_NAMES.OUTDOORS,
  [GROUP_TYPES.PASTURE[TYPE_KEYS.URL_KEY]]: DATA_FILTERS_BUILTIN_NAMES.PASTURE,
});

const CERTIFICATION_LINKS = Object.freeze({
  [CERTIFICATION_NAMES.ANIMAL_WELFARE_APPROVED]: "https://agreenerworld.org/certifications/animal-welfare-approved/",
  [CERTIFICATION_NAMES.AGW_CERTIFIED_GRASSFED]: "https://agreenerworld.org/certifications/certified-grass-fed/",
  [CERTIFICATION_NAMES.AGW_CERTIFIED_NONGMO]: "https://agreenerworld.org/certifications/certified-nongmo-agw/",
  [CERTIFICATION_NAMES.AGW_CERTIFIED_ORGANIC]: "https://agreenerworld.org/certifications/certified-organic-by-agw/",
  [CERTIFICATION_NAMES.AGW_CERTIFIED_REGENERATIVE]: "https://agreenerworld.org/certifications/certified-regenerative/",
  [CERTIFICATION_NAMES.AGW_SALMON_WELFARE_CERTIFIED]: "https://agreenerworld.org/certifications/animal-welfare-approved/",
  [CERTIFICATION_NAMES.CERTIFIED_HUMANE]: "https://certifiedhumane.org/overview/",
  [CERTIFICATION_NAMES.GAP]: "https://globalanimalpartnership.org/",
  [CERTIFICATION_NAMES.GAP_FISH]: "https://globalanimalpartnership.org/standards/salmon/",
  [CERTIFICATION_NAMES.APPLEGATE_HUMANELY_RAISED]: "https://applegate.com/mission/animal-welfare",
  [CERTIFICATION_NAMES.USDA_ORGANIC]: "https://www.ams.usda.gov/",
  [CERTIFICATION_NAMES.AMERICAN_GRASSFED_ASSOCIATION]: "https://www.americangrassfed.org/about-us/our-standards/",
  [CERTIFICATION_NAMES.AMERICAN_HUMANE_CERTIFIED]: "https://www.americanhumane.org/humane-heartland/our-standards/",
  [CERTIFICATION_NAMES.FOOD_ALLIANCE_CERTIFIED]: "https://foodalliance.org/livestock-producers/",
  [CERTIFICATION_NAMES.ONE_HEALTH_CERTIFIED]: "https://onehealthcertified.org/",
  [CERTIFICATION_NAMES.REGENERATIVE_ORGANIC_CERTIFIED]: "https://regenorganic.org/why-regenerative-organic/",
  [CERTIFICATION_NAMES.CERTIFIED_HUMANE_APPROVED_HATCHERIES]: "https://certifiedhumane.org/our-standards/",
  [CERTIFICATION_NAMES.BETTER_CHICKEN_PROJECT]: "https://globalanimalpartnership.org/better-chicken/",
});

const FILTER_CERTIFICATION_NAMES = Object.freeze({
  ANIMAL_WELFARE_APPROVED: "animal_welfare_approved",
  AGW_CERTIFIED_REGENERATIVE: "agw_certified_regenerative",
  AGW_CERTIFIED_GRASSFED: "agw_certified_grassfed",
  AGW_CERTIFIED_NONGMO: "agw_certified_non_gmo",
  AGW_CERTIFIED_ORGANIC: "agw_certified_organic",
  AGW_SALMON_WELFARE_CERTIFIED: "agw_salmon_welfare_certified",
  REGENERATIVE_ORGANIC_CERTIFIED: "regenerative_organic_certified",
  CERTIFIED_HUMANE: "certified_humane",
  CERTIFIED_HUMANE_FREE_RANGE: "certified_humane_free_range",
  CERTIFIED_HUMANE_GRASS_FED: "certified_humane_grass_fed",
  CERTIFIED_HUMANE_PASTURE_RAISED: "certified_humane_pasture_raised_grass_fed",
  CERTIFIED_HUMANE_APPROVED_HATCHERIES: "certified_humane_approved_hatcheries",
  ANIMAL_WELFARE_CERTIFIED_1: "animal_welfare_certified_1",
  ANIMAL_WELFARE_CERTIFIED_2: "animal_welfare_certified_2",
  ANIMAL_WELFARE_CERTIFIED_3: "animal_welfare_certified_3",
  ANIMAL_WELFARE_CERTIFIED_4: "animal_welfare_certified_4",
  ANIMAL_WELFARE_CERTIFIED_5: "animal_welfare_certified_5",
  ANIMAL_WELFARE_CERTIFIED_6: "animal_welfare_certified_6",
  BETTER_CHICKEN_PROJECT: "better_chicken_project",
  ANIMAL_WELFARE_CERTIFIED_FISH_1: "animal_welfare_certified_fish_1",
  ANIMAL_WELFARE_CERTIFIED_FISH_3: "animal_welfare_certified_fish_3",
  ANIMAL_WELFARE_CERTIFIED_FISH_6: "animal_welfare_certified_fish_6",
  APPLEGATE_HUMANELY_RAISED: "applegate_humanely_raised",
  SELF_CERTIFIED: "self_certified",
  AMERICAN_HUMANE_CERTIFIED: "american_humane_certified",
  AMERICAN_GRASSFED_ASSOCIATION: "american_grassfed_association",
  USDA_ORGANIC: "usda_organic",
  FOOD_ALLIANCE_CERTIFIED: "food_alliance_certified",
  ONE_HEALTH_CERTIFIED: "one_health_certified",
});

const FILTER_TO_CERTIFICATION_MAP = Object.freeze({
  [FILTER_CERTIFICATION_NAMES.ANIMAL_WELFARE_APPROVED]: CERTIFICATION_NAMES.ANIMAL_WELFARE_APPROVED,
  [FILTER_CERTIFICATION_NAMES.AGW_CERTIFIED_REGENERATIVE]: CERTIFICATION_NAMES.AGW_CERTIFIED_REGENERATIVE,
  [FILTER_CERTIFICATION_NAMES.AGW_CERTIFIED_GRASSFED]: CERTIFICATION_NAMES.AGW_CERTIFIED_GRASSFED,
  [FILTER_CERTIFICATION_NAMES.AGW_CERTIFIED_NONGMO]: CERTIFICATION_NAMES.AGW_CERTIFIED_NONGMO,
  [FILTER_CERTIFICATION_NAMES.AGW_CERTIFIED_ORGANIC]: CERTIFICATION_NAMES.AGW_CERTIFIED_ORGANIC,
  [FILTER_CERTIFICATION_NAMES.AGW_SALMON_WELFARE_CERTIFIED]: CERTIFICATION_NAMES.AGW_SALMON_WELFARE_CERTIFIED,
  [FILTER_CERTIFICATION_NAMES.REGENERATIVE_ORGANIC_CERTIFIED]: CERTIFICATION_NAMES.REGENERATIVE_ORGANIC_CERTIFIED,
  [FILTER_CERTIFICATION_NAMES.CERTIFIED_HUMANE]: CERTIFICATION_NAMES.CERTIFIED_HUMANE,
  [FILTER_CERTIFICATION_NAMES.CERTIFIED_HUMANE_FREE_RANGE]: CERTIFICATION_NAMES.CERTIFIED_HUMANE,
  [FILTER_CERTIFICATION_NAMES.CERTIFIED_HUMANE_GRASS_FED]: CERTIFICATION_NAMES.CERTIFIED_HUMANE,
  [FILTER_CERTIFICATION_NAMES.CERTIFIED_HUMANE_PASTURE_RAISED]: CERTIFICATION_NAMES.CERTIFIED_HUMANE,
  [FILTER_CERTIFICATION_NAMES.CERTIFIED_HUMANE_APPROVED_HATCHERIES]: CERTIFICATION_NAMES.CERTIFIED_HUMANE_APPROVED_HATCHERIES,
  [FILTER_CERTIFICATION_NAMES.ANIMAL_WELFARE_CERTIFIED_1]: CERTIFICATION_NAMES.GAP,
  [FILTER_CERTIFICATION_NAMES.ANIMAL_WELFARE_CERTIFIED_2]: CERTIFICATION_NAMES.GAP,
  [FILTER_CERTIFICATION_NAMES.ANIMAL_WELFARE_CERTIFIED_3]: CERTIFICATION_NAMES.GAP,
  [FILTER_CERTIFICATION_NAMES.ANIMAL_WELFARE_CERTIFIED_4]: CERTIFICATION_NAMES.GAP,
  [FILTER_CERTIFICATION_NAMES.ANIMAL_WELFARE_CERTIFIED_5]: CERTIFICATION_NAMES.GAP,
  [FILTER_CERTIFICATION_NAMES.ANIMAL_WELFARE_CERTIFIED_6]: CERTIFICATION_NAMES.GAP,
  [FILTER_CERTIFICATION_NAMES.BETTER_CHICKEN_PROJECT]: CERTIFICATION_NAMES.BETTER_CHICKEN_PROJECT,
  [FILTER_CERTIFICATION_NAMES.ANIMAL_WELFARE_CERTIFIED_FISH_1]: CERTIFICATION_NAMES.GAP_FISH,
  [FILTER_CERTIFICATION_NAMES.ANIMAL_WELFARE_CERTIFIED_FISH_3]: CERTIFICATION_NAMES.GAP_FISH,
  [FILTER_CERTIFICATION_NAMES.ANIMAL_WELFARE_CERTIFIED_FISH_6]: CERTIFICATION_NAMES.GAP_FISH,
  [FILTER_CERTIFICATION_NAMES.APPLEGATE_HUMANELY_RAISED]: CERTIFICATION_NAMES.APPLEGATE_HUMANELY_RAISED,
  [FILTER_CERTIFICATION_NAMES.SELF_CERTIFIED]: CERTIFICATION_NAMES.SELF_CERTIFIED,
  [FILTER_CERTIFICATION_NAMES.AMERICAN_HUMANE_CERTIFIED]: CERTIFICATION_NAMES.AMERICAN_HUMANE_CERTIFIED,
  [FILTER_CERTIFICATION_NAMES.AMERICAN_GRASSFED_ASSOCIATION]: CERTIFICATION_NAMES.AMERICAN_GRASSFED_ASSOCIATION,
  [FILTER_CERTIFICATION_NAMES.USDA_ORGANIC]: CERTIFICATION_NAMES.USDA_ORGANIC,
  [FILTER_CERTIFICATION_NAMES.FOOD_ALLIANCE_CERTIFIED]: CERTIFICATION_NAMES.FOOD_ALLIANCE_CERTIFIED,
  [FILTER_CERTIFICATION_NAMES.ONE_HEALTH_CERTIFIED]: CERTIFICATION_NAMES.ONE_HEALTH_CERTIFIED,
});


// Update FilterSlider.FILTER_SLIDER_DEFAULT is updating the default
const DEFAULT_FILTER_NAMES = Object.freeze({
  DEFAULT: "Default: Outdoor Access" + ASPCA_RECOMMENDED_SUFFIX,
  ASPCA_BASELINE: "Indoors with Enrichments" + ASPCA_RECOMMENDED_SUFFIX,
  ASPCA_BETTER: "Outdoor Access" + ASPCA_RECOMMENDED_SUFFIX,
  ASPCA_BEST: "Pasture" + ASPCA_RECOMMENDED_SUFFIX,
  ANY: "Any certification",
});

const DEFAULT_FILTER_DESCRIPTIONS = Object.freeze({
  [GROUP_TYPES.PASTURE[TYPE_KEYS.URL_KEY]]: "This is the best recommendation from the ASPCA. In addition to other welfare improvements, animals have reliable access to pasture.",
  [GROUP_TYPES.OUTDOORS[TYPE_KEYS.URL_KEY]]: "Find Humane's default filter shows pasture-raised and outdoor-access certified products. This is the second-best recommendation from the ASPCA. In addition to other welfare improvements, animals are either raised on pasture or have access to more space, fresh air, and sunlight compared to traditional industrial environments. The product selection is limited but it fits into our vision of animals that aren't always confined indoors.",
  [GROUP_TYPES.INDOORS[TYPE_KEYS.URL_KEY]]: "This is the baseline recommendation from the ASPCA for meaningful welfare certification programs with unacceptable industrial practices banned. This may include animals raised in industrial environments without outdoor access although with improved indoor conditions. This also includes pasture-raised and outdoor-access certified products.",
});

const DEFAULT_FILTER_SUBTITLES = Object.freeze({
  [GROUP_TYPES.PASTURE[TYPE_KEYS.URL_KEY]]: "Animals raised on pasture or in industrial environments with more space, fresh air, and sunlight",
  [GROUP_TYPES.OUTDOORS[TYPE_KEYS.URL_KEY]]: "Animals raised on pasture or in industrial environments with access to more space, fresh air, and sunlight",
  [GROUP_TYPES.INDOORS[TYPE_KEYS.URL_KEY]]: "Meaningfully more humane but may include industrial environments without outdoor access although with improved indoor conditions",
});

const DEFAULT_FILTER_SHORT_DESCRIPTIONS = Object.freeze({
  [DEFAULT_FILTER_NAMES.DEFAULT]: "Outdoor Access +",
  [DEFAULT_FILTER_NAMES.ASPCA_BASELINE]: "Indoors with Enrichments +",
  [DEFAULT_FILTER_NAMES.ASPCA_BETTER]: "Outdoor Access +",
  [DEFAULT_FILTER_NAMES.ASPCA_BEST]: "Pasture",
  [DEFAULT_FILTER_NAMES.ANY]: "All",
});

const FILTER_NONE_SAVED = "None saved";

// All certifications with a value of `true` to allow a
// simple map lookup to check if a key is a certification
const CERTIFICATIONS_MAP = Object.freeze({
  animal_welfare_approved: true,
  agw_certified_regenerative: true,
  agw_certified_grassfed: true,
  agw_certified_non_gmo: true,
  agw_certified_organic: true,
  agw_salmon_welfare_certified: true,
  certified_humane: true,
  certified_humane_free_range: true,
  certified_humane_grass_fed: true,
  certified_humane_pasture_raised_grass_fed: true,
  animal_welfare_certified_1: true,
  animal_welfare_certified_2: true,
  animal_welfare_certified_3: true,
  animal_welfare_certified_4: true,
  animal_welfare_certified_5: true,
  animal_welfare_certified_6: true,
  better_chicken_project: true,
  animal_welfare_certified_fish_1: true,
  animal_welfare_certified_fish_3: true,
  animal_welfare_certified_fish_6: true,
  applegate_humanely_raised: true,
  american_humane_certified: true,
  american_grassfed_association: true,
  usda_organic: true,
  food_alliance_certified: true,
  one_health_certified: true,
  self_certified: true,
  nopartial: true,
  regenerative_organic_certified: true,
  certified_humane_approved_hatcheries: true,
});

const FILTER_RECOMMENDED_VALUES_UNIQUE = Object.freeze({
  [GROUP_TYPES.OUTDOORS[TYPE_KEYS.URL_KEY]]: {
    certified_humane_free_range: true,
    animal_welfare_certified_3: true,
  },

  [GROUP_TYPES.INDOORS[TYPE_KEYS.URL_KEY]]: {
    certified_humane: true,
    animal_welfare_certified_2: true,
    regenerative_organic_certified: true,
  },

  [GROUP_TYPES.PASTURE[TYPE_KEYS.URL_KEY]]: {
    animal_welfare_approved: true,
    certified_humane_grass_fed: true,
    certified_humane_pasture_raised_grass_fed: true,
    animal_welfare_certified_4: true,
    animal_welfare_certified_5: true,
    animal_welfare_certified_6: true,
  },
});

function createDataFilter(filter) {
  return Object.assign(
    {},
    filter,
    {
      [SYNTHESIZED_TYPES.QUERY_ID[TYPE_KEYS.URL_KEY]]: uuidv4(),
    },
  );
}

const MAP_PIN_NAMES = Object.freeze({
  GENERIC: "Generic",
  CART: "Cart",
  DINING: "Dining",
  COFFEE_SHOP: "Coffee Shop",
  FARMERS_MARKET: "Farmers Market",
  FARM: "Farm",
  SEARCH: "Search",
  GPS: "GPS",
});

// Match humane_location.rb } getMapPinType
const MAP_PIN_TYPES = Object.freeze([
  MAP_PIN_NAMES.GENERIC,
  MAP_PIN_NAMES.CART,
  MAP_PIN_NAMES.DINING,
  MAP_PIN_NAMES.COFFEE_SHOP,
  MAP_PIN_NAMES.FARMERS_MARKET,
  MAP_PIN_NAMES.FARM,
  MAP_PIN_NAMES.SEARCH,
  MAP_PIN_NAMES.GPS,
]);

function getDefaultDataFilter() {
  return createDataFilter(DATA_FILTERS_BUILTIN_OPTIONS[DATA_FILTERS_BUILTIN_DEFAULT]);
}

const TEMPORARY_SNACK_DEFAULT_DURATION = 7000;

const DEFAULT_STATE = Object.freeze({
  locations: {},
  locations_online: null,
  ads: [],
  adRefreshInterval: 15000,
  hideAds: false,
  lightMode: false,
  extraDebug: IS_DEVELOPMENT,
  lastMainView: SCREENS.MAP_STACK,
  deliveryLocationFilter: "",
  firstZoom: ZOOM_LEVELS.CITY + 1,
  initialCenter: CENTER_UNITED_STATES,
  initialZoom: ZOOM_LEVELS.CONTINENT,
  shouldAskToMoveToCurrentLocation: true,
  shouldShowSearch: true,
  detailedZoom: ZOOM_LEVELS.BUILDINGS - 4,
  mapCenter: CENTER_UNITED_STATES,
  showTabs: true,
  showHeader: true,
  showFilterSlider: true,
  showZoomControls: true,
  moveMap: null,
  lastGPSLocation: null,
  dataFilter: getDefaultDataFilter(),
});

const NUMBER_OF_FILTER_IMAGES = 3;
const FILTER_IMAGE_WIDTH = 32;
const FILTER_IMAGES_GAP = 5;
const FILTER_IMAGES_WRAPPER_PADDING = 2;

function getFilterImagesSize() {
  return ((NUMBER_OF_FILTER_IMAGES * FILTER_IMAGE_WIDTH) + ((NUMBER_OF_FILTER_IMAGES - 1) * FILTER_IMAGES_GAP)) + (FILTER_IMAGES_WRAPPER_PADDING * 2);
}

export {
  createDataFilter,
  VERSION,
  MAJOR_VERSION_NUMBER,
  MINOR_VERSION_NUMBER,
  PATCH_VERSION_NUMBER,
  IS_DEVELOPMENT,
  DEFAULT_DEBUG_LEVEL,
  APPCONFIG,
  STYLES,
  LOGIN_STATE,
  DEFAULT_LOGIN_STATE,
  DISPATCH,
  ZOOM_LEVELS,
  CENTER_UNITED_STATES,
  DEFAULT_ZOOMED,
  MAP_FEATURES,
  SCREENS,
  SCREEN_LINKS,
  REACT_NATIVE_PAPER_THEME,
  FILTER_RECOMMENDED_VALUES_UNIQUE,
  FILTER_NONE_SAVED,
  DEFAULT_STATE,
  LOCAL_DATA_KEYS,
  CERTIFICATIONS_MAP,
  APPLE_STORE_LINK,
  ANDROID_STORE_LINK,
  CERTIFICATION_NAMES,
  DEFAULT_FILTER_NAMES,
  PRODUCT_TYPES,
  SUBSTACK_LINK,
  TWITTER_LINK,
  HOMEPAGE_LINK,
  INSTAGRAM_LINK,
  DEFAULT_FILTER_DESCRIPTIONS,
  ASPCA_RECOMMENDED_SUFFIX,
  DEFAULT_FILTER_SUBTITLES,
  UTM_SOURCES,
  UTM_MEDIUMS,
  UTM_CAMPAIGNS,
  HEADER_TEXT_OVERFLOW_LENGTH,
  DEFAULT_FILTER_SHORT_DESCRIPTIONS,
  SIMPLE_GLOBAL_STATE,
  FACEBOOK_LINK,
  SMALL_SCREEN_WIDTH1,
  CERTIFICATION_LINKS,
  FILTER_CERTIFICATION_NAMES,
  FILTER_TO_CERTIFICATION_MAP,
  B_CORP_LINK,
  GRANT_LINK,
  GRANT_LINK_2023,
  SUBSCRIBE_POPUP_DELAY_MILLISECONDS,
  DATA_FILTERS_BUILTIN_DEFAULT,
  DATA_FILTERS_BUILTIN_NAMES,
  DATA_FILTERS_BUILTIN_OPTIONS,
  GOOGLE_API_KEY_AUTOCOMPLETE,
  DATA_FILTERS_BUILTIN_REVERSE_MAP,
  TYPE_KEYS,
  SYNTHESIZED_TYPES,
  GROUP_TYPES,
  EXTRA_ANIMAL_WELFARE,
  INGREDIENTS,
  WELFARE_CERTIFICATIONS,
  LOCATION_FLAGS,
  SCREEN_TITLES,
  MAP_PIN_NAMES,
  MAP_PIN_TYPES,
  getBuiltinOptionsIndoorsPlus,
  getBuiltinOptionsOutdoorsPlus,
  getBuiltinOptionsPasture,
  clearBuiltinOptions,
  getDefaultDataFilter,
  TEMPORARY_SNACK_DEFAULT_DURATION,
  EMAIL_REGEX,
  FILTER_IMAGE_WIDTH,
  FILTER_IMAGES_GAP,
  FILTER_IMAGES_WRAPPER_PADDING,
  getFilterImagesSize,
}
